// httpClient.js
// Base HTTP client with uniform request structure and UUID generation

import axios from 'axios';
import eventBus from './eventBus';
import SessionService from './sessionService';
import {
    EVENT_UPDATE_ACTION_BAR_VIEW,
    EVENT_UPDATE_AUTHENTICATED
} from '../utils/constants';

export const BASE_URL = process.env.REACT_APP_API_BASE_URL;
// export const BASE_URL = 'http://localhost:8004/api/v1';

export const clearStaleAuthentication = () => {
    const sessionService = SessionService.getInstance();

    if (!sessionService.hasSessionToken()) {
        return;
    }

    sessionService.clearSession();
    eventBus.emit(EVENT_UPDATE_AUTHENTICATED, false);
    eventBus.emit(EVENT_UPDATE_ACTION_BAR_VIEW, 'login');
};

const isAuthenticationFailure = (error) => {
    const requestUrl = String(error?.config?.url || '');
    const requestHeaders = error?.config?.headers || {};

    if (
        requestHeaders['X-STATSPYDER-ADMIN-KEY'] ||
        requestHeaders['x-statspyder-admin-key'] ||
        requestUrl.includes('/admin/') ||
        requestUrl.includes('/reports/activity')
    ) {
        return false;
    }

    const status = error?.response?.status;
    const responseData = error?.response?.data || {};
    const errorText = String(responseData.error || responseData.message || '').toLowerCase();

    return status === 401 || (
        status === 403 &&
        /auth|credential|login|session|token|unauthor/.test(errorText)
    );
};

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (isAuthenticationFailure(error)) {
            clearStaleAuthentication();
        }

        return Promise.reject(error);
    }
);

/**
 * Generate a unique request ID using crypto.randomUUID()
 * @returns {string} UUID v4 string
 */
const generateRequestId = () => {
    return crypto.randomUUID();
};

/**
 * Get headers for API requests
 * Authenticated requests send the access JWT while cookies remain enabled for
 * endpoints that still rely on browser session state during the transition.
 * @returns {Object} Headers object
 */
const getHeaders = () => {
    const sessionService = SessionService.getInstance();
    const accessToken = sessionService.isAuthenticated() ? sessionService.getAccessToken() : null;
    const headers = {
        'Content-Type': 'application/json'
    };

    if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
    }
    
    return headers;
};

/**
 * Create a uniform request object with requestId
 * @param {Object} data - Request data to include
 * @returns {Object} Request object with requestId
 */
export const createRequest = (data = {}) => {
    return {
        requestId: generateRequestId(),
        ...data
    };
};

/**
 * POST request with uniform request structure
 * Cookies are automatically sent with requests (withCredentials: true)
 * @param {string} url - API endpoint URL
 * @param {Object} data - Request data (will be wrapped in createRequest)
 * @returns {Promise} Axios response
 */
export const POST = async (url, data = {}) => {
    const requestData = createRequest(data);
    return axios.post(url, requestData, {
        headers: getHeaders(),
        withCredentials: true // Enable sending cookies with cross-origin requests
    });
};

/**
 * GET request with uniform request structure
 * Cookies are automatically sent with requests (withCredentials: true)
 * @param {string} url - API endpoint URL
 * @param {Object} params - Query parameters (optional)
 * @returns {Promise} Axios response
 */
export const GET = async (url, params = {}) => {
    // For GET requests, we can include requestId as a query parameter
    const queryParams = {
        requestId: generateRequestId(),
        ...params
    };
    
    return axios.get(url, {
        headers: getHeaders(),
        params: queryParams,
        withCredentials: true // Enable sending cookies with cross-origin requests
    });
};

// export default {
//     POST,
//     GET,
//     createRequest,
//     BASE_URL
// };
